/**
 * Pure H OS · Sprint 6 (integration) · Entity Registry & Cross-Module Links.
 *
 * A single-file registry that turns the platform from six loosely-related
 * modules into ONE operating system: given any entity ID (SKU, batch code,
 * campaign, supplier, customer name, invoice number) it returns the deep
 * links to every place that entity appears across Commerce, Operations,
 * Marketing, and Finance.
 *
 * It ALSO exports `ENTITY_INDEX` — a flat, palette-ready list of every
 * first-class entity in the platform, grouped by type. The Command Palette
 * uses this list to let a user type "Nocturne Amber" and jump straight to
 * the product, batch, revenue row, or invoice.
 *
 * No visual language introduced. No CSS. No new primitives.
 */
import type { LucideIcon } from "lucide-react";
import {
  Package, Factory, Megaphone, FileText, CreditCard, Users,
  ShoppingCart, TrendingUp, Wallet, Warehouse, Sparkles,
} from "lucide-react";

import { PRODUCTS, ORDERS, STOCK, SIGNALS } from "./commerceData";
import { BATCHES, SUPPLIERS } from "./operationsData";
import { CAMPAIGNS, CREATORS } from "./marketingData";
import { INVOICES, VENDOR_PAYMENTS, REVENUE_STREAMS } from "./financeData";

// ── Types ────────────────────────────────────────────────────────
export type EntityKind =
  | "product" | "campaign" | "batch" | "supplier"
  | "customer" | "invoice" | "payment" | "creator";

export type RelatedLink = {
  label: string;
  href: string;
  module: "commerce" | "operations" | "marketing" | "finance";
  icon: LucideIcon;
  testId?: string;
};

const ICON_FOR_MODULE: Record<RelatedLink["module"], LucideIcon> = {
  commerce:   ShoppingCart,
  operations: Factory,
  marketing:  Megaphone,
  finance:    Wallet,
};

// ── Family / base-SKU helpers ───────────────────────────────────
// SKUs like "ORV-NA-001-50" and "ORV-NA-001-15" share the base "ORV-NA-001".
export function skuFamily(sku: string): string {
  const parts = sku.split("-");
  return parts.length >= 3 ? parts.slice(0, 3).join("-") : sku;
}

function productShortName(sku: string): string {
  const p = PRODUCTS.find((x) => x.sku === sku);
  return p?.name ?? sku;
}

// ── Related links: PRODUCT (by SKU) ─────────────────────────────
export function linksForProductSku(sku: string): RelatedLink[] {
  const links: RelatedLink[] = [];
  const family = skuFamily(sku);

  // Marketing campaigns that promote this SKU (or its family)
  CAMPAIGNS
    .filter((c) => c.sku === family || c.sku === sku)
    .forEach((c) => {
      links.push({
        label: `Campaign · ${c.name}`,
        href: `/pureh/marketing/calendar?q=${encodeURIComponent(c.name)}`,
        module: "marketing",
        icon: Megaphone,
        testId: `link-cmp-${c.id}`,
      });
    });

  // Operations batches making this product
  BATCHES
    .filter((b) => b.sku === sku || skuFamily(b.sku) === family)
    .forEach((b) => {
      links.push({
        label: `Batch · ${b.code}`,
        href: `/pureh/operations/batches?open=${b.id}`,
        module: "operations",
        icon: Factory,
        testId: `link-batch-${b.id}`,
      });
    });

  // Commerce inventory row
  if (STOCK.some((s) => s.sku === sku)) {
    links.push({
      label: `Inventory · ${sku}`,
      href: `/pureh/commerce/inventory?q=${encodeURIComponent(sku)}`,
      module: "commerce",
      icon: Warehouse,
      testId: `link-inv-${sku}`,
    });
  }

  // Finance revenue stream is aggregated at channel level, not SKU. Link to
  // the Finance Revenue page filtered by product name so the top-SKU table
  // highlights the row.
  if (PRODUCTS.some((p) => p.sku === sku)) {
    links.push({
      label: "Revenue · Finance",
      href: `/pureh/finance/revenue?q=${encodeURIComponent(productShortName(sku))}`,
      module: "finance",
      icon: TrendingUp,
      testId: `link-rev-${sku}`,
    });
  }

  return links;
}

// ── Related links: BATCH (by batch id) ──────────────────────────
export function linksForBatch(batchId: string): RelatedLink[] {
  const b = BATCHES.find((x) => x.id === batchId);
  if (!b) return [];
  const links: RelatedLink[] = [];

  // Product in Commerce
  const p = PRODUCTS.find((x) => x.sku === b.sku);
  if (p) {
    links.push({
      label: `Product · ${p.name}`,
      href: `/pureh/commerce/products?open=${p.id}`,
      module: "commerce",
      icon: Package,
      testId: `link-product-${p.id}`,
    });
  }

  // QC / Packaging surfaces
  links.push({
    label: `QC checks · ${b.code}`,
    href: `/pureh/operations/quality?q=${encodeURIComponent(b.code)}`,
    module: "operations",
    icon: Sparkles,
    testId: `link-qc-${b.id}`,
  });
  links.push({
    label: `Packaging queue · ${b.code}`,
    href: `/pureh/operations/packaging?q=${encodeURIComponent(b.code)}`,
    module: "operations",
    icon: Package,
    testId: `link-pack-${b.id}`,
  });

  return links;
}

// ── Related links: SUPPLIER ─────────────────────────────────────
export function linksForSupplier(supplierId: string): RelatedLink[] {
  const s = SUPPLIERS.find((x) => x.id === supplierId);
  if (!s) return [];
  const links: RelatedLink[] = [];

  // Vendor Payments in Finance (matched by name)
  if (VENDOR_PAYMENTS.some((v) => v.vendor === s.name)) {
    links.push({
      label: `Payments · ${s.name}`,
      href: `/pureh/finance/vendors?q=${encodeURIComponent(s.name)}`,
      module: "finance",
      icon: CreditCard,
      testId: `link-vp-${s.id}`,
    });
  }

  // Movements in Operations (receipts / PO refs)
  links.push({
    label: `Movements · ${s.name}`,
    href: `/pureh/operations/movement?q=${encodeURIComponent(s.name)}`,
    module: "operations",
    icon: Warehouse,
    testId: `link-mv-${s.id}`,
  });

  return links;
}

// ── Related links: SIGNAL (customer purchase signal) ────────────
export function linksForSignalCustomer(customer: string, campaignHint?: string): RelatedLink[] {
  const links: RelatedLink[] = [];

  // Orders by this customer
  if (ORDERS.some((o) => o.customer === customer)) {
    links.push({
      label: `Orders · ${customer}`,
      href: `/pureh/commerce/orders?q=${encodeURIComponent(customer)}`,
      module: "commerce",
      icon: ShoppingCart,
      testId: `link-orders-${customer}`,
    });
  }

  // Best campaign match (by hint or first live)
  const cmp = campaignHint
    ? CAMPAIGNS.find((c) => c.name.toLowerCase().includes(campaignHint.toLowerCase()))
    : CAMPAIGNS.find((c) => c.status === "live");
  if (cmp) {
    links.push({
      label: `Campaign · ${cmp.name}`,
      href: `/pureh/marketing/calendar?q=${encodeURIComponent(cmp.name)}`,
      module: "marketing",
      icon: Megaphone,
      testId: `link-cmp-${cmp.id}`,
    });
  }

  // Invoices if the customer is on wholesale side
  if (INVOICES.some((i) => i.customer === customer)) {
    links.push({
      label: `Invoices · ${customer}`,
      href: `/pureh/finance/invoices?q=${encodeURIComponent(customer)}`,
      module: "finance",
      icon: FileText,
      testId: `link-inv-${customer}`,
    });
  }

  return links;
}

// ── Related links: INVOICE ──────────────────────────────────────
export function linksForInvoice(invoiceId: string): RelatedLink[] {
  const inv = INVOICES.find((x) => x.id === invoiceId);
  if (!inv) return [];
  const links: RelatedLink[] = [];

  // Commerce orders from the same customer
  if (ORDERS.some((o) => o.customer === inv.customer)) {
    links.push({
      label: `Order · ${inv.customer}`,
      href: `/pureh/commerce/orders?q=${encodeURIComponent(inv.customer)}`,
      module: "commerce",
      icon: ShoppingCart,
      testId: `link-order-${inv.id}`,
    });
  }

  // Purchase signals for the same customer
  if (SIGNALS.some((s) => s.customer === inv.customer)) {
    links.push({
      label: `Signals · ${inv.customer}`,
      href: `/pureh/commerce/signals?q=${encodeURIComponent(inv.customer)}`,
      module: "commerce",
      icon: Sparkles,
      testId: `link-sig-${inv.id}`,
    });
  }

  return links;
}

// ── Related links: VENDOR PAYMENT ───────────────────────────────
export function linksForVendorPayment(paymentId: string): RelatedLink[] {
  const vp = VENDOR_PAYMENTS.find((x) => x.id === paymentId);
  if (!vp) return [];
  const links: RelatedLink[] = [];

  const sup = SUPPLIERS.find((s) => s.name === vp.vendor);
  if (sup) {
    links.push({
      label: `Supplier · ${sup.name}`,
      href: `/pureh/operations/suppliers?open=${sup.id}`,
      module: "operations",
      icon: Users,
      testId: `link-sup-${sup.id}`,
    });
  }

  // Expenses ledger match by vendor
  links.push({
    label: `Expense ledger · ${vp.vendor}`,
    href: `/pureh/finance/expenses?q=${encodeURIComponent(vp.vendor)}`,
    module: "finance",
    icon: FileText,
    testId: `link-exp-${vp.id}`,
  });

  return links;
}

// ── Related links: CAMPAIGN ─────────────────────────────────────
export function linksForCampaign(campaignId: string): RelatedLink[] {
  const c = CAMPAIGNS.find((x) => x.id === campaignId);
  if (!c) return [];
  const links: RelatedLink[] = [];

  // Featured product
  const p = PRODUCTS.find((x) => skuFamily(x.sku) === c.sku);
  if (p) {
    links.push({
      label: `Product · ${p.name}`,
      href: `/pureh/commerce/products?open=${p.id}`,
      module: "commerce",
      icon: Package,
      testId: `link-p-${p.id}`,
    });
  }

  // Revenue attribution
  links.push({
    label: "Revenue attribution",
    href: `/pureh/finance/revenue?q=${encodeURIComponent(c.name.split(" · ")[0])}`,
    module: "finance",
    icon: TrendingUp,
    testId: `link-rev-${c.id}`,
  });

  return links;
}

// ── Flat entity index (used by Command Palette + Global Search) ─
export type EntityIndexRow = {
  id: string;
  kind: EntityKind;
  title: string;
  subtitle: string;
  href: string;
  keywords: string[];
};

// The index is derived from the live PRODUCTS / CAMPAIGNS / … arrays
// (which are themselves live views over MongoDB), so a plain
// `const ENTITY_INDEX = [...].map(...)` computed at module-import time
// would freeze empty. Instead expose a Proxy that recomputes on any
// access — negligible cost at typical UI cardinalities (~150 rows).
function computeEntityIndex(): EntityIndexRow[] {
  return [
    // Products
    ...PRODUCTS.map<EntityIndexRow>((p) => ({
      id: `ent-p-${p.id}`,
      kind: "product",
      title: p.name,
      subtitle: `${p.sku} · EGP ${p.priceEGP.toLocaleString()} · ${p.status}`,
      href: `/pureh/commerce/products?open=${p.id}`,
      keywords: [p.sku, p.name.toLowerCase(), p.family, skuFamily(p.sku)],
    })),
    // Campaigns
    ...CAMPAIGNS.map<EntityIndexRow>((c) => ({
      id: `ent-c-${c.id}`,
      kind: "campaign",
      title: c.name,
      subtitle: `${c.channels.join(", ")} · ${c.status}`,
      href: `/pureh/marketing/calendar?q=${encodeURIComponent(c.name)}`,
      keywords: [c.name.toLowerCase(), c.sku, c.owner, ...c.channels.map((x) => x.toLowerCase())],
    })),
    // Batches
    ...BATCHES.map<EntityIndexRow>((b) => ({
      id: `ent-b-${b.id}`,
      kind: "batch",
      title: `${b.code} · ${b.productName}`,
      subtitle: `${b.units} units · ${b.stage} · ${b.masterPerfumer}`,
      href: `/pureh/operations/batches?open=${b.id}`,
      keywords: [b.code.toLowerCase(), b.productName.toLowerCase(), b.sku, b.stage],
    })),
    // Suppliers
    ...SUPPLIERS.map<EntityIndexRow>((s) => ({
      id: `ent-s-${s.id}`,
      kind: "supplier",
      title: s.name,
      subtitle: `${s.city} · ${s.category} · OTD ${Math.round(s.onTimeRate * 100)}%`,
      href: `/pureh/operations/suppliers?open=${s.id}`,
      keywords: [s.name.toLowerCase(), s.city.toLowerCase(), s.category.toLowerCase()],
    })),
    // Customers (from orders — unique)
    ...Array.from(new Set(ORDERS.map((o) => o.customer))).map<EntityIndexRow>((c) => ({
      id: `ent-cu-${c}`,
      kind: "customer",
      title: c,
      subtitle: "Commerce customer",
      href: `/pureh/commerce/orders?q=${encodeURIComponent(c)}`,
      keywords: [c.toLowerCase()],
    })),
    // Invoices
    ...INVOICES.map<EntityIndexRow>((i) => ({
      id: `ent-i-${i.id}`,
      kind: "invoice",
      title: `${i.number} · ${i.customer}`,
      subtitle: `${i.status} · EGP ${i.amountEGP.toLocaleString()} · ${i.dueRelative}`,
      href: `/pureh/finance/invoices?open=${i.id}`,
      keywords: [i.number.toLowerCase(), i.customer.toLowerCase(), i.status],
    })),
    // Payments
    ...VENDOR_PAYMENTS.map<EntityIndexRow>((v) => ({
      id: `ent-vp-${v.id}`,
      kind: "payment",
      title: `${v.reference} · ${v.vendor}`,
      subtitle: `${v.status} · EGP ${v.amountEGP.toLocaleString()} · ${v.dueRelative}`,
      href: `/pureh/finance/vendors?open=${v.id}`,
      keywords: [v.reference.toLowerCase(), v.vendor.toLowerCase(), v.status],
    })),
    // Creators
    ...CREATORS.map<EntityIndexRow>((c) => ({
      id: `ent-cr-${c.id}`,
      kind: "creator",
      title: c.name,
      subtitle: `${c.handle} · ${c.tier} · ${c.audience.toLocaleString()} audience`,
      href: `/pureh/marketing/creators?q=${encodeURIComponent(c.name)}`,
      keywords: [c.name.toLowerCase(), c.handle.toLowerCase(), c.tier],
    })),
  ];
}

export const ENTITY_INDEX: EntityIndexRow[] = new Proxy([] as EntityIndexRow[], {
  get(_target, prop, receiver) {
    const src = computeEntityIndex();
    const v = Reflect.get(src, prop, receiver);
    return typeof v === "function" ? (v as (...args: unknown[]) => unknown).bind(src) : v;
  },
  has(_target, prop) { return prop in computeEntityIndex(); },
  ownKeys(_target)   { return Reflect.ownKeys(computeEntityIndex()); },
  getOwnPropertyDescriptor(_t, prop) {
    return Object.getOwnPropertyDescriptor(computeEntityIndex(), prop);
  },
});

export const ENTITY_KIND_META: Record<EntityKind, { label: string; icon: LucideIcon; module: RelatedLink["module"] }> = {
  product:  { label: "Products",  icon: Package,      module: "commerce"   },
  campaign: { label: "Campaigns", icon: Megaphone,    module: "marketing"  },
  batch:    { label: "Batches",   icon: Factory,      module: "operations" },
  supplier: { label: "Suppliers", icon: Users,        module: "operations" },
  customer: { label: "Customers", icon: Users,        module: "commerce"   },
  invoice:  { label: "Invoices",  icon: FileText,     module: "finance"    },
  payment:  { label: "Payments",  icon: CreditCard,   module: "finance"    },
  creator:  { label: "Creators",  icon: Sparkles,     module: "marketing"  },
};

// Convenience: quickly get the module icon for a `RelatedLink`.
export function iconForModule(m: RelatedLink["module"]): LucideIcon {
  return ICON_FOR_MODULE[m];
}
